/* ═══════════════════════════════════════════════════════════════
   ALTERLY — Invert Node
   Inverts image color channels with alpha preservation.
   ═══════════════════════════════════════════════════════════════ */

import type { NodeDefinition } from '../types';
import { GLSL_HEADER } from '../../rendering/effects/glslCommon';
import { createShaderProcessor } from '../../rendering/effects/shaderProcessor';

const INVERT_SHADER = `${GLSL_HEADER}
uniform sampler2D u_image;
uniform bool u_invertAlpha;

void main() {
  vec4 src = texture(u_image, v_uv);
  vec3 invertedRgb = vec3(1.0) - src.rgb;
  float alpha = u_invertAlpha ? (1.0 - src.a) : src.a;
  fragColor = vec4(invertedRgb, alpha);
}
`;

export const invertNode: NodeDefinition = {
  type: 'color.invert',
  category: 'Color',
  label: 'Invert',
  description: 'Inverts colors to create a photographic negative effect',
  keywords: ['invert', 'negative', 'reverse', 'color', 'opposite'],
  targetBackend: 'webgl2',
  inputs: [
    { id: 'image', name: 'Source', type: 'image' },
  ],
  outputs: [
    { id: 'output', name: 'Output', type: 'image' },
  ],
  params: [
    {
      id: 'invertAlpha',
      name: 'Invert Alpha',
      type: 'boolean',
      defaultValue: false,
      uiHint: 'toggle',
    },
  ],
  processor: createShaderProcessor({
    fragmentShader: INVERT_SHADER,
    getUniforms: (inputs, params) => ({
      u_image: inputs.image as unknown as import('../../rendering/types').TextureHandle,
      u_invertAlpha: Boolean(params.invertAlpha),
    }),
  }),
};
